/**
 * Timeframe utilities for Roadmap Builder.
 * Computes fiscal-year-aware date labels for building block cards.
 */

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_ABBR  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export interface YearStart {
  month: number; // 1–12
  day: number;   // 1–28
}

export const YEAR_PRESETS: Array<{ label: string; month: number; day: number }> = [
  { label: "Calendar Year (Jan 1)",      month: 1,  day: 1 },
  { label: "UK / India Fiscal (Apr 1)",  month: 4,  day: 1 },
  { label: "US Government Fiscal (Oct 1)", month: 10, day: 1 },
  { label: "Academic Year (Sep 1)",      month: 9,  day: 1 },
];

/** True if a YearStart matches a preset; false = "Custom". */
export function isCustomYear(ys: YearStart): boolean {
  return !YEAR_PRESETS.some(p => p.month === ys.month && p.day === ys.day);
}

/** Format an ISO date string as "Dec 2030". */
export function formatPeakDate(peakDateIso: string): string {
  const [year, month] = peakDateIso.split("-").map(Number);
  return `${MONTH_ABBR[month - 1]} ${year}`;
}

/** Format an ISO date string as "December 2030". */
export function formatPeakDateLong(peakDateIso: string): string {
  const [year, month] = peakDateIso.split("-").map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** Parse an ISO date string to a Date object (day set to 1 to avoid timezone issues). */
function parseIso(iso: string): Date {
  const [year, month] = iso.split("-").map(Number);
  return new Date(year, month - 1, 1);
}

/**
 * Return the fiscal year number for a given date.
 * For calendar year (month=1): fiscal year === calendar year.
 * For April fiscal (month=4): Apr 2025–Mar 2026 === FY2026.
 */
export function getFiscalYear(date: Date, ys: YearStart): number {
  const m = date.getMonth() + 1; // 1-indexed
  if (ys.month === 1) return date.getFullYear();
  return m >= ys.month ? date.getFullYear() + 1 : date.getFullYear();
}

/**
 * Return { quarter (1–4), startMonth (1-indexed), endMonth (1-indexed) }
 * for the fiscal quarter containing `date`.
 */
export function getFiscalQuarter(date: Date, ys: YearStart): {
  quarter: number;
  startMonth: number;
  endMonth: number;
  startYear: number;
  endYear: number;
} {
  const m = date.getMonth() + 1;
  // Offset of this month from fiscal year start (0-indexed within the fiscal year)
  const offset = ((m - ys.month + 12) % 12);
  const quarter = Math.floor(offset / 3) + 1;

  // Start month of this quarter
  const startMonth = ((ys.month + (quarter - 1) * 3 - 1) % 12) + 1;
  const endMonth   = ((startMonth + 2 - 1) % 12) + 1;

  // Determine years for start/end months
  const fy = getFiscalYear(date, ys);
  // In a non-calendar fiscal year, the start of FY is in the previous calendar year if startMonth >= ys.month
  let startYear: number;
  let endYear: number;
  if (ys.month === 1) {
    startYear = date.getFullYear();
    endYear   = startMonth <= endMonth ? startYear : startYear + 1;
  } else {
    // FY starts at ys.month in calendar year (fy - 1)
    const fyStartCalYear = fy - 1;
    startYear = startMonth >= ys.month ? fyStartCalYear : fy;
    endYear   = endMonth >= ys.month ? fyStartCalYear : fy;
  }

  return { quarter, startMonth, endMonth, startYear, endYear };
}

/**
 * Return the date-range label for the NEXT fiscal quarter.
 * Reuses getBlockDateLabel by advancing today ~92 days into the next quarter.
 */
export function getNextQuarterDateLabel(
  peakDateIso: string | null | undefined,
  yearStartMonth: number,
  yearStartDay: number,
  today: Date = new Date(),
): string | null {
  const nextQuarterDate = new Date(today.getTime() + 92 * 24 * 60 * 60 * 1000);
  return getBlockDateLabel("quarterlyObjectives", peakDateIso, yearStartMonth, yearStartDay, nextQuarterDate);
}

/** Build the year prefix ("FY" or ""). */
function fyPrefix(ys: YearStart): string {
  return ys.month === 1 ? "" : "FY";
}

/**
 * Compute the date-range label to show on a building block card.
 * Returns null for blocks that are timeless (vision, mission) or when no peak date is set.
 */
export function getBlockDateLabel(
  blockKey: string,
  peakDateIso: string | null | undefined,
  yearStartMonth: number,
  yearStartDay: number,
  today: Date = new Date(),
): string | null {
  if (!peakDateIso) return null;

  const ys: YearStart = { month: yearStartMonth, day: yearStartDay };
  const peak = parseIso(peakDateIso);
  const peakYear = peak.getFullYear();
  const prefix = fyPrefix(ys);

  const monthsToGo = (peakYear - today.getFullYear()) * 12 + (peak.getMonth() - today.getMonth());

  switch (blockKey) {
    case "vision":
    case "mission":
      return null;

    case "peakAmbition":
      return `Target: ${formatPeakDate(peakDateIso)}`;

    case "peakAmbitionObjectives":
      return `What must be true by ${peakYear}`;

    case "threeYearObjectives": {
      if (monthsToGo < 36) {
        // Short runway
        const curFY = getFiscalYear(today, ys);
        const nextFY = curFY + 1;
        if (monthsToGo <= 12) {
          return `Pre-Peak (${prefix}${peakYear})`;
        }
        return `Pre-Peak (${prefix}${nextFY}–${prefix}${peakYear - 1})`;
      }
      const start = peakYear - 2;
      return `${prefix}${start}–${prefix}${peakYear}`;
    }

    case "oneYearObjectives": {
      const fy = getFiscalYear(today, ys);
      return `${prefix}${fy}`;
    }

    case "quarterlyObjectives": {
      const fy = getFiscalYear(today, ys);
      const { quarter, startMonth, endMonth } = getFiscalQuarter(today, ys);
      const monthRange = `${MONTH_ABBR[startMonth - 1]}–${MONTH_ABBR[endMonth - 1]}`;
      if (ys.month === 1) {
        return `Q${quarter} ${fy} (${monthRange})`;
      }
      return `Q${quarter} FY${fy} (${monthRange})`;
    }

    default:
      return null;
  }
}
